import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Pencil, Trash2, Loader2, Send, RefreshCw, BarChart2, Copy,
  CheckCircle2, Clock, XCircle, FileEdit, MessageCircle, Settings2, Layers,
  GitBranch, AlertCircle, PauseCircle, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  APPROVED: 'bg-emerald-100 text-emerald-700',
  PENDING: 'bg-amber-100 text-amber-700',
  SUBMITTED: 'bg-amber-100 text-amber-700',
  DRAFT: 'bg-slate-100 text-slate-600',
  REJECTED: 'bg-rose-100 text-rose-700',
  PAUSED: 'bg-orange-100 text-orange-700',
  DISABLED: 'bg-orange-100 text-orange-700',
  SUBMISSION_FAILED: 'bg-rose-100 text-rose-700',
};
const STATUS_ICON = { APPROVED: CheckCircle2, PENDING: Clock, SUBMITTED: Clock, DRAFT: FileEdit, REJECTED: XCircle, PAUSED: PauseCircle, DISABLED: PauseCircle, SUBMISSION_FAILED: XCircle };

const renderPreview = (body, variables, samples) =>
  (body || '').replace(/\{\{(\d+)\}\}/g, (m, n) => {
    const v = (samples || [])[Number(n) - 1];
    return v && String(v).trim() !== '' ? String(v) : `‹${(variables || [])[Number(n) - 1] || n}›`;
  });

// ---- Config status panel ---------------------------------------------------
const ConfigPanel = ({ status }) => {
  const [diag, setDiag] = useState(null);
  const [checking, setChecking] = useState(false);
  const [linking, setLinking] = useState(false);
  const [minResult, setMinResult] = useState(null);
  const [testing, setTesting] = useState(false);
  if (!status) return null;
  const copy = (v, label) => { navigator.clipboard.writeText(v).then(() => toast.success(`${label} copied`)); };

  const runDiagnostics = async () => {
    setChecking(true); setDiag(null);
    try { const r = await axios.get(`${BACKEND_URL}/api/whatsapp/diagnostics`); setDiag(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Diagnostics failed'); }
    finally { setChecking(false); }
  };
  const runMinimalTest = async () => {
    setTesting(true); setMinResult(null);
    try { const r = await axios.post(`${BACKEND_URL}/api/whatsapp/templates/test-minimal`); setMinResult(r.data);
      if (r.data?.ok) toast.success(`Minimal template reached Meta (id ${r.data.id})`); else toast.error('Minimal test failed — see details'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Minimal test failed'); }
    finally { setTesting(false); }
  };
  const linkApp = async () => {
    setLinking(true);
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/subscribe-app`);
      toast.success('App subscribed to WABA — webhooks will now be delivered here');
      runDiagnostics();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Subscribe failed'); }
    finally { setLinking(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5" data-testid="wa-config-panel">
      <div className="flex items-center gap-2 mb-3">
        <Settings2 className="h-4 w-4 text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-700">Cloud API connection</h3>
        <Badge className={`${status.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'} border-0`}>
          {status.configured ? 'Ready to send' : 'Not fully configured'}
        </Badge>
        <Button size="sm" variant="outline" onClick={runDiagnostics} disabled={checking} className="ml-auto" data-testid="wa-diagnostics-btn">
          {checking ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Check connection
        </Button>
        <Button size="sm" variant="outline" onClick={runMinimalTest} disabled={testing} data-testid="wa-minimal-test-btn">
          {testing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Send minimal test
        </Button>
      </div>
      {!status.configured && status.missing?.length > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
          Missing secrets: <span className="font-mono">{status.missing.join(', ')}</span> — add these in platform Secrets, then restart/redeploy.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Webhook callback URL (paste into Meta)</Label>
          <div className="flex gap-1">
            <Input readOnly value={status.webhookUrl || ''} className="h-9 text-xs font-mono" data-testid="wa-webhook-url" />
            <Button size="sm" variant="outline" onClick={() => copy(status.webhookUrl, 'Webhook URL')}><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Verify token (paste into Meta)</Label>
          <div className="flex gap-1">
            <Input readOnly value={status.verifyToken || ''} className="h-9 text-xs font-mono" data-testid="wa-verify-token" />
            <Button size="sm" variant="outline" onClick={() => copy(status.verifyToken, 'Verify token')}><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        Graph {status.graphVersion} · App ID {status.appId || '—'} · WABA {status.wabaId || '—'} · signature {status.signatureEnforced ? 'enforced' : 'not enforced yet (add META_APP_SECRET)'} · number ••••{status.phoneNumberIdTail}
      </p>

      {diag && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="wa-diagnostics-result">
          {!diag.configured ? (
            <p className="text-amber-700">{diag.message}</p>
          ) : (
            <div className="space-y-1.5">
              <p className="flex items-center gap-1.5">
                {diag.tokenValid ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
                Access token {diag.tokenValid ? 'valid' : `invalid — ${diag.tokenError}`}
              </p>
              {diag.phoneNumber && (
                <p className="text-xs text-slate-600 ml-6">
                  Number: {diag.phoneNumber.display_phone_number} · {diag.phoneNumber.verified_name} · quality {diag.phoneNumber.quality_rating || 'n/a'}
                </p>
              )}
              <p className="flex items-center gap-1.5">
                {diag.appLinkedToWaba ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  : diag.appLinkedToWaba === false ? <XCircle className="h-4 w-4 text-rose-600" />
                  : <Clock className="h-4 w-4 text-slate-400" />}
                App {diag.appId} {diag.appLinkedToWaba ? 'is linked to' : diag.appLinkedToWaba === false ? 'is NOT linked to' : 'linkage unknown for'} WABA {diag.wabaId}
              </p>
              {diag.subscribedAppsReadable === false && (
                <p className="text-xs text-rose-600 ml-6">Could not read subscribed apps: {diag.subscribedAppsError}</p>
              )}
              {diag.appLinkedToWaba === false && (
                <div className="ml-6 mt-1">
                  <p className="text-xs text-slate-500 mb-1">This is why Meta's webhook Configuration screen may be hidden. Link the app now:</p>
                  <Button size="sm" onClick={linkApp} disabled={linking} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-subscribe-app-btn">
                    {linking ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null} Link app to WABA
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {minResult && (
        <div className={`mt-3 rounded-lg border p-3 text-sm ${minResult.ok ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`} data-testid="wa-minimal-test-result">
          {minResult.ok ? (
            <p className="text-emerald-800 font-medium">✓ Minimal template reached Meta — template id {minResult.id} (status {minResult.status || 'PENDING'}). Payload is valid; the CRM→Meta connection works.</p>
          ) : (
            <div className="text-rose-800">
              <p className="font-semibold">✗ Minimal test rejected by the API (code {minResult.details?.code}{minResult.details?.error_subcode ? `/${minResult.details.error_subcode}` : ''})</p>
              <p className="text-xs mt-0.5">{minResult.details?.error_user_msg || minResult.details?.message}</p>
              {minResult.details?.fbtrace_id && <p className="text-[10px] text-rose-500 mt-0.5">fbtrace_id: {minResult.details.fbtrace_id}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---- Template create/edit dialog ------------------------------------------
const TemplateDialog = ({ open, onOpenChange, initial, categories, variableCatalogue, buttonUrlVariables, publicBaseUrl, onSaved }) => {
  const isEdit = Boolean(initial?.id);
  const isRevise = Boolean(initial?._reviseFromId);
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState('Invoice & Payments');
  const [language, setLanguage] = useState('en');
  const [body, setBody] = useState('');
  const [variables, setVariables] = useState([]);
  const [samples, setSamples] = useState([]);
  const [isReminder, setIsReminder] = useState(false);
  const [button, setButton] = useState(null);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setDisplayName(initial?.displayName || '');
    setCategory(initial?.category || categories[0] || 'Invoice & Payments');
    setLanguage(initial?.language || 'en');
    setBody(initial?.bodyText || '');
    setVariables(initial?.variables || []);
    setSamples(initial?.sampleValues || []);
    setIsReminder(Boolean(initial?.isPaymentReminder));
    const b0 = (initial?.buttons || [])[0];
    setButton(b0 ? {
      text: b0.text || 'View',
      baseUrl: b0.baseUrl || publicBaseUrl || '',
      urlVariable: b0.urlVariable || (buttonUrlVariables[0]?.token || 'quote_link'),
      urlExample: b0.urlExample || '',
    } : null);
  }, [open, initial, categories, buttonUrlVariables, publicBaseUrl]);

  const addButton = () => {
    const first = buttonUrlVariables[0] || { token: 'quote_link', example: '' };
    setButton({ text: 'View', baseUrl: publicBaseUrl || '', urlVariable: first.token, urlExample: first.example || '' });
  };
  const changeButtonVar = (tok) => {
    const v = buttonUrlVariables.find((x) => x.token === tok);
    setButton((b) => ({ ...b, urlVariable: tok, urlExample: v?.example || b.urlExample || '' }));
  };
  const buttonPreviewUrl = button ? `${button.baseUrl || ''}${button.urlExample || '{{1}}'}` : '';

  const insertVar = (token) => {
    const n = variables.length + 1;
    const chip = `{{${n}}}`;
    const el = bodyRef.current;
    const start = el?.selectionStart ?? body.length;
    const next = body.slice(0, start) + chip + body.slice(start);
    setBody(next);
    setVariables((v) => [...v, token]);
    setSamples((s) => [...s, '']);
  };

  const removeLastVar = () => {
    if (variables.length === 0) return;
    const n = variables.length;
    setBody((b) => b.replace(new RegExp(`\\{\\{${n}\\}\\}`), ''));
    setVariables((v) => v.slice(0, -1));
    setSamples((s) => s.slice(0, -1));
  };

  const save = async () => {
    if (!displayName.trim()) { toast.error('Template name is required'); return; }
    if (!body.trim()) { toast.error('Message body is required'); return; }
    setSaving(true);
    const payload = {
      displayName: displayName.trim(), category, language,
      bodyText: body.trim(), variables, sampleValues: samples, isPaymentReminder: isReminder,
      buttons: button ? [{
        type: 'URL', text: (button.text || 'View').trim(), urlType: 'dynamic',
        baseUrl: button.baseUrl || '', urlVariable: button.urlVariable, urlExample: (button.urlExample || '').trim(),
      }] : [],
      ...(isRevise ? { revisedFromTemplateId: initial._reviseFromId } : {}),
    };
    try {
      if (isEdit) {
        const r = await axios.put(`${BACKEND_URL}/api/whatsapp/templates/${initial.id}`, payload);
        toast.success(r.data?.newVersion ? 'New version created (previous stays sendable until this is approved)' : 'Template updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/whatsapp/templates`, payload);
        toast.success(isRevise ? 'Revised draft created — review, then submit to Meta' : 'Template created (draft)');
      }
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="wa-template-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit template' : isRevise ? 'Revise template (new draft)' : 'New template'}</DialogTitle>
          <DialogDescription>{isRevise ? 'Pre-filled from the rejected/paused template. Edit the content to address Meta’s feedback, then submit this new draft.' : 'Reuse existing wording. All templates submit to Meta as UTILITY.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Template name *</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Payment reminder" className="h-10" data-testid="wa-tpl-name" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-10" data-testid="wa-tpl-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Message body</Label>
            <Textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Hi {{1}}, ..." className="text-sm" data-testid="wa-tpl-body" />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-slate-400">Insert a variable (adds the next {`{{n}}`}):</p>
              {variables.length > 0 && <button onClick={removeLastVar} className="text-xs text-rose-500" data-testid="wa-tpl-remove-var">Remove last variable</button>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {variableCatalogue.map((v) => (
                <button key={v.token} type="button" onClick={() => insertVar(v.token)} title={v.label}
                  className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 text-[11px] font-mono hover:bg-teal-100" data-testid={`wa-tpl-var-${v.token}`}>
                  {v.token}
                </button>
              ))}
            </div>
          </div>

          {variables.length > 0 && (
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Sample values (shown to Meta &amp; in preview)</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="wa-tpl-samples">
                {variables.map((tok, i) => (
                  <div key={i}>
                    <Label className="text-[11px] text-slate-400 font-mono">{`{{${i + 1}}} = ${tok}`}</Label>
                    <Input value={samples[i] || ''} onChange={(e) => setSamples((s) => s.map((x, idx) => idx === i ? e.target.value : x))} className="h-9 text-sm" data-testid={`wa-tpl-sample-${i}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Live preview</Label>
            <div className="rounded-xl bg-[#e7ffdb] border border-emerald-200 px-4 py-3 text-sm text-slate-800 whitespace-pre-line" data-testid="wa-tpl-preview">
              {renderPreview(body, variables, samples)}
              {button && (
                <div className="mt-3 -mx-4 -mb-3 border-t border-emerald-200 px-4 py-2 text-center text-sm font-medium text-sky-700 flex items-center justify-center gap-1.5" data-testid="wa-tpl-preview-button">
                  <ExternalLink className="h-3.5 w-3.5" /> {button.text || 'View'}
                </div>
              )}
            </div>
            {button && buttonPreviewUrl && (
              <p className="text-[11px] text-slate-400 mt-1 font-mono break-all" data-testid="wa-tpl-preview-url">→ {buttonPreviewUrl}</p>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-3" data-testid="wa-tpl-buttons-section">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm font-semibold text-slate-700">Buttons</Label>
              {!button ? (
                <Button type="button" size="sm" variant="outline" onClick={addButton} className="h-7 text-xs" data-testid="wa-tpl-add-button">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Button
                </Button>
              ) : (
                <button type="button" onClick={() => setButton(null)} className="text-xs text-rose-500" data-testid="wa-tpl-remove-button">Remove button</button>
              )}
            </div>
            {!button ? (
              <p className="text-xs text-slate-400">Optional. Add one “Visit Website” call-to-action button with a dynamic link (e.g. View Quote / Pay Now).</p>
            ) : (
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] text-slate-400 block mb-1">Button Type</Label>
                    <Input value="Visit Website" readOnly className="h-9 text-sm bg-slate-50" data-testid="wa-tpl-button-type" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-400 block mb-1">Button Text</Label>
                    <Input value={button.text} maxLength={25} onChange={(e) => setButton((b) => ({ ...b, text: e.target.value }))} placeholder="View Quote" className="h-9 text-sm" data-testid="wa-tpl-button-text" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] text-slate-400 block mb-1">URL Type</Label>
                    <Input value="Dynamic URL" readOnly className="h-9 text-sm bg-slate-50" data-testid="wa-tpl-button-urltype" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-400 block mb-1">Dynamic value {`{{1}}`}</Label>
                    <Select value={button.urlVariable} onValueChange={changeButtonVar}>
                      <SelectTrigger className="h-9 text-sm" data-testid="wa-tpl-button-var"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {buttonUrlVariables.map((v) => <SelectItem key={v.token} value={v.token}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] text-slate-400 block mb-1">Base URL (the dynamic value is appended as {`{{1}}`})</Label>
                  <Input value={button.baseUrl} onChange={(e) => setButton((b) => ({ ...b, baseUrl: e.target.value }))} placeholder="https://crm.travcations.com/" className="h-9 text-sm font-mono" data-testid="wa-tpl-button-base" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-400 block mb-1">Example value (shown to Meta at submission)</Label>
                  <Input value={button.urlExample} onChange={(e) => setButton((b) => ({ ...b, urlExample: e.target.value }))} placeholder="ITIMYWNPZ/preview" className="h-9 text-sm font-mono" data-testid="wa-tpl-button-example" />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
            <Switch checked={isReminder} onCheckedChange={setIsReminder} data-testid="wa-tpl-reminder" />
            <div>
              <p className="text-sm font-medium text-slate-700">Payment reminder</p>
              <p className="text-xs text-slate-400">Blocked automatically for proforma invoices (BATCH 2 rule).</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="wa-tpl-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} {isEdit ? 'Save' : isRevise ? 'Create revised draft' : 'Create draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Template row ----------------------------------------------------------
const TemplateRow = ({ tpl, onEdit, onSubmit, onSync, onInsights, onDelete, onRevise, onViewError, busyId }) => {
  const failed = tpl.status === 'submission_failed';
  const st = failed ? 'SUBMISSION_FAILED' : (tpl.metaStatus || 'DRAFT').toUpperCase();
  const Icon = STATUS_ICON[st] || FileEdit;
  const busy = busyId === tpl.id;
  const canSubmit = st === 'DRAFT' || st === 'SUBMISSION_FAILED';
  const canRevise = st === 'REJECTED' || st === 'PAUSED' || st === 'DISABLED';
  const canEdit = st === 'DRAFT' || st === 'SUBMISSION_FAILED' || st === 'APPROVED';
  return (
    <div className="flex items-start gap-3 px-4 py-3 border-t border-slate-100 first:border-0 hover:bg-slate-50" data-testid={`wa-tpl-row-${tpl.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-800 text-sm">{tpl.displayName}</span>
          <Badge className={`${STATUS_BADGE[st] || STATUS_BADGE.DRAFT} border-0 text-[10px] flex items-center gap-1`} data-testid={`wa-tpl-status-${tpl.id}`}>
            <Icon className="h-3 w-3" /> {st.replace(/_/g, ' ')}
          </Badge>
          {tpl.version > 1 && <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px]">v{tpl.version}</Badge>}
          {tpl.isActiveVersion && <Badge className="bg-emerald-50 text-emerald-600 border-0 text-[10px]">active</Badge>}
          {tpl.isPaymentReminder && <Badge className="bg-orange-50 text-orange-600 border-0 text-[10px]">reminder</Badge>}
          {(tpl.buttons || []).length > 0 && <Badge className="bg-sky-50 text-sky-600 border-0 text-[10px] flex items-center gap-0.5" data-testid={`wa-tpl-cta-${tpl.id}`}><ExternalLink className="h-2.5 w-2.5" /> {tpl.buttons[0].text || 'CTA'}</Badge>}
          {tpl.revisedByTemplateId && <Badge className="bg-sky-50 text-sky-600 border-0 text-[10px]" data-testid={`wa-tpl-revised-${tpl.id}`}>revised</Badge>}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-line line-clamp-2">{tpl.bodyText}</p>
        {failed && tpl.submissionError && (
          <button onClick={() => onViewError(tpl)} className="text-[11px] text-rose-600 underline underline-offset-2 mt-0.5 text-left" data-testid={`wa-tpl-submit-error-${tpl.id}`}>
            Submission failed (code {tpl.submissionError.code}) — view details
          </button>
        )}
        {!failed && st === 'REJECTED' && tpl.rejectionReason && (
          <p className="text-[11px] text-rose-500 mt-0.5">Rejected by Meta: {tpl.rejectionReason}</p>
        )}
        {!failed && (st === 'PAUSED' || st === 'DISABLED') && (
          <p className="text-[11px] text-orange-600 mt-0.5">{st === 'PAUSED' ? 'Paused by Meta' : 'Disabled by Meta'}{tpl.rejectionReason ? `: ${tpl.rejectionReason}` : ''}</p>
        )}
        {tpl.lastSyncedAt && <p className="text-[10px] text-slate-400 mt-0.5">Last synced {new Date(tpl.lastSyncedAt).toLocaleString()}</p>}
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {canSubmit && (
          <Button variant="ghost" size="sm" onClick={() => onSubmit(tpl)} disabled={busy} className="text-emerald-600" title="Submit to Meta" data-testid={`wa-tpl-submit-${tpl.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        )}
        {canRevise && (
          <Button variant="ghost" size="sm" onClick={() => onRevise(tpl)} className="text-sky-600" title="Create a revised draft" data-testid={`wa-tpl-revise-${tpl.id}`}>
            <GitBranch className="h-4 w-4" />
          </Button>
        )}
        {failed && tpl.submissionError && (
          <Button variant="ghost" size="sm" onClick={() => onViewError(tpl)} className="text-rose-500" title="View submission error" data-testid={`wa-tpl-error-btn-${tpl.id}`}>
            <AlertCircle className="h-4 w-4" />
          </Button>
        )}
        {tpl.metaTemplateId && (
          <Button variant="ghost" size="sm" onClick={() => onSync(tpl)} disabled={busy} className="text-slate-500" title="Sync status from Meta" data-testid={`wa-tpl-sync-${tpl.id}`}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
        {tpl.metaTemplateId && (
          <Button variant="ghost" size="sm" onClick={() => onInsights(tpl)} className="text-slate-500" title="Meta performance" data-testid={`wa-tpl-insights-${tpl.id}`}>
            <BarChart2 className="h-4 w-4" />
          </Button>
        )}
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={() => onEdit(tpl)} className="text-slate-500 hover:text-teal-600" title={st === 'APPROVED' ? 'Edit (creates a new version)' : 'Edit'} data-testid={`wa-tpl-edit-${tpl.id}`}>
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => onDelete(tpl)} className="text-slate-500 hover:text-rose-600" title="Delete" data-testid={`wa-tpl-delete-${tpl.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Module-level to avoid the visual-edits Babel recursion on nested inline maps.
const TemplateGroup = ({ cat, rows, busyId, onEdit, onSubmit, onSync, onInsights, onDelete, onRevise, onViewError }) => {
  const list = rows || [];
  return (
    <div data-testid={`wa-tpl-group-${cat}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Badge className="bg-slate-100 text-slate-600 border-0 text-[11px]">{cat}</Badge>
        <span className="text-xs text-slate-400">{list.length}</span>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {list.map((t) => (
          <TemplateRow key={t.id} tpl={t} busyId={busyId}
            onEdit={onEdit} onSubmit={onSubmit} onSync={onSync} onInsights={onInsights}
            onDelete={onDelete} onRevise={onRevise} onViewError={onViewError} />
        ))}
      </div>
    </div>
  );
};

// ---- Quick replies section -------------------------------------------------
const QuickRepliesSection = () => {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${BACKEND_URL}/api/whatsapp/quick-replies`).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!title.trim() || !body.trim()) { toast.error('Title and body required'); return; }
    setSaving(true);
    try { await axios.post(`${BACKEND_URL}/api/whatsapp/quick-replies`, { title: title.trim(), body: body.trim() }); setTitle(''); setBody(''); load(); toast.success('Quick reply added'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const del = async (id) => {
    try { await axios.delete(`${BACKEND_URL}/api/whatsapp/quick-replies/${id}`); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mt-6" data-testid="wa-quick-replies-admin">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-slate-400" /> Quick replies (canned session responses)</h3>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Label (e.g. Thanks)" className="h-9 text-sm sm:w-40" data-testid="wa-qr-title" />
        <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message text" className="h-9 text-sm flex-1" data-testid="wa-qr-body" />
        <Button size="sm" onClick={add} disabled={saving} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="wa-qr-add"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <div className="space-y-1">
        {items.map((q) => (
          <div key={q.id} className="flex items-center justify-between text-sm px-3 py-1.5 rounded-lg bg-slate-50" data-testid={`wa-qr-item-${q.id}`}>
            <span><span className="font-medium text-slate-700">{q.title}</span> <span className="text-slate-500">— {q.body}</span></span>
            <button onClick={() => del(q.id)} className="text-slate-400 hover:text-rose-600" data-testid={`wa-qr-del-${q.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---- Page ------------------------------------------------------------------
const WhatsAppTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ categories: [], variables: [] });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [insights, setInsights] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [submittingAll, setSubmittingAll] = useState(false);
  const [submitAllResult, setSubmitAllResult] = useState(null);

  const load = async () => {
    try {
      const [t, m, s] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/whatsapp/templates`),
        axios.get(`${BACKEND_URL}/api/whatsapp/templates/meta`),
        axios.get(`${BACKEND_URL}/api/whatsapp/config-status`),
      ]);
      setTemplates(Array.isArray(t.data) ? t.data : []);
      setMeta(m.data || { categories: [], variables: [] });
      setStatus(s.data || null);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (tpl) => {
    setBusyId(tpl.id);
    try { const r = await axios.post(`${BACKEND_URL}/api/whatsapp/templates/${tpl.id}/submit`); toast.success(`Submitted — Meta status ${r.data.metaStatus || 'PENDING'}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); load(); } finally { setBusyId(''); }
  };
  const sync = async (tpl) => {
    setBusyId(tpl.id);
    try { const r = await axios.post(`${BACKEND_URL}/api/whatsapp/templates/${tpl.id}/sync-status`); toast.success(`Meta status: ${r.data.metaStatus}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); } finally { setBusyId(''); }
  };
  const showInsights = async (tpl) => {
    try { const r = await axios.get(`${BACKEND_URL}/api/whatsapp/templates/${tpl.id}/insights`); setInsights({ name: tpl.displayName, data: r.data }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Insights unavailable'); }
  };
  const startRevise = (tpl) => {
    setEditing({ ...tpl, id: undefined, _reviseFromId: tpl.id });
    setDialogOpen(true);
  };
  const viewError = (tpl) => setErrorDetail({ name: tpl.displayName, err: tpl.submissionError || {} });
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try { await axios.delete(`${BACKEND_URL}/api/whatsapp/templates/${deleteTarget.id}`); toast.success('Deleted'); setDeleteTarget(null); load(); }
    catch { toast.error('Delete failed'); }
  };
  const submitAll = async () => {
    const pending = templates.filter((t) => (t.metaStatus || '').toUpperCase() === 'DRAFT').length;
    if (pending === 0) { toast.info('No draft templates to submit'); return; }
    if (!window.confirm(`Submit ${pending} draft template(s) to Meta for approval? Approved, pending, rejected and paused ones are skipped (rejected/paused must be revised).`)) return;
    setSubmittingAll(true); setSubmitAllResult(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/whatsapp/templates/submit-all`);
      setSubmitAllResult(r.data);
      toast.success(`Submitted ${r.data.submitted}/${r.data.total} to Meta`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit-all failed'); }
    finally { setSubmittingAll(false); }
  };

  const groups = useMemo(() => {
    const g = {};
    templates.forEach((t) => { (g[t.category] = g[t.category] || []).push(t); });
    return (meta.categories || []).filter((c) => (g[c] || []).length).map((c) => ({ cat: c, rows: g[c] }));
  }, [templates, meta.categories]);

  return (
    <div className="p-4 sm:p-6 max-w-5xl" data-testid="whatsapp-templates-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Layers className="h-6 w-6 text-emerald-600" /> WhatsApp Templates</h1>
          <p className="text-sm text-slate-500">Meta Cloud API templates — admin only. Agents can only send approved ones.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={submitAll} disabled={submittingAll} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="wa-tpl-submit-all-btn">
            {submittingAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Submit all to Meta
          </Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="wa-tpl-add-btn">
            <Plus className="h-4 w-4 mr-1" /> New template
          </Button>
        </div>
      </div>

      <ConfigPanel status={status} />

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : (
        <div className="space-y-5">
          {groups.map((grp) => (
            <TemplateGroup key={grp.cat} cat={grp.cat} rows={grp.rows} busyId={busyId}
              onEdit={(x) => { setEditing(x); setDialogOpen(true); }}
              onSubmit={submit} onSync={sync} onInsights={showInsights} onDelete={setDeleteTarget}
              onRevise={startRevise} onViewError={viewError} />
          ))}
        </div>
      )}

      <QuickRepliesSection />

      {dialogOpen && (
        <TemplateDialog open onOpenChange={setDialogOpen} initial={editing}
          categories={meta.categories || []} variableCatalogue={meta.variables || []}
          buttonUrlVariables={meta.buttonUrlVariables || []} publicBaseUrl={meta.publicBaseUrl || ''}
          onSaved={load} />
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="wa-tpl-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.displayName}”?</AlertDialogTitle>
            <AlertDialogDescription>This removes the CRM copy. If it was approved at Meta, the Meta template is not deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="wa-tpl-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={Boolean(insights)} onOpenChange={(o) => !o && setInsights(null)}>
        <DialogContent className="max-w-lg" data-testid="wa-insights-dialog">
          <DialogHeader><DialogTitle>Meta performance · {insights?.name}</DialogTitle></DialogHeader>
          <pre className="text-xs bg-slate-50 rounded-lg p-3 overflow-x-auto max-h-80">{JSON.stringify(insights?.data, null, 2)}</pre>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(errorDetail)} onOpenChange={(o) => !o && setErrorDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="wa-error-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertCircle className="h-4 w-4 text-rose-600" /> Submission error · {errorDetail?.name}</DialogTitle>
            <DialogDescription>The Meta API rejected this submission before content review (a request/payload error) — this is not a content Rejection. Fix the issue and resubmit.</DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1.5">
            <p><span className="text-slate-400">Error code:</span> <span className="font-mono">{errorDetail?.err?.code ?? '—'}{errorDetail?.err?.error_subcode ? `/${errorDetail.err.error_subcode}` : ''}</span></p>
            {errorDetail?.err?.error_user_title && <p><span className="text-slate-400">Title:</span> {errorDetail.err.error_user_title}</p>}
            <p><span className="text-slate-400">Message:</span> {errorDetail?.err?.error_user_msg || errorDetail?.err?.message || '—'}</p>
            {errorDetail?.err?.fbtrace_id && <p><span className="text-slate-400">Meta trace id:</span> <span className="font-mono text-[11px]">{errorDetail.err.fbtrace_id}</span></p>}
            {errorDetail?.err?.timestamp && <p className="text-[11px] text-slate-400">Recorded {new Date(errorDetail.err.timestamp).toLocaleString()}</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(submitAllResult)} onOpenChange={(o) => !o && setSubmitAllResult(null)}>
        <DialogContent className="max-w-lg" data-testid="wa-submit-all-result">
          <DialogHeader><DialogTitle>Submitted to Meta</DialogTitle></DialogHeader>
          <p className="text-sm font-semibold text-slate-700">Submitted {submitAllResult?.submitted} · Failed {submitAllResult?.failed} of {submitAllResult?.total}</p>
          <p className="text-xs text-slate-400 mb-1">Meta review typically takes minutes to ~24h. Use "Check connection" / the Sync button to refresh statuses (the webhook also updates them automatically).</p>
          <div className="space-y-0.5 max-h-72 overflow-y-auto">
            {(submitAllResult?.results || []).map((r) => (
              <p key={r.id} className={`text-xs ${r.ok ? 'text-emerald-700' : 'text-rose-600'}`}>
                {r.displayName}: {r.ok ? (r.metaStatus || 'PENDING') : (typeof r.reason === 'string' ? r.reason : 'failed')}
              </p>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppTemplates;
